import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Eye, ChevronDown, ChevronUp, Loader2 } from "lucide-react";

/** Compact audit-trail card showing the last N impersonation events.
 *  Collapsible so it stays out of the way on the Users tab. */
export default function ImpersonationLogCard() {
  const [logs, setLogs] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/impersonation-logs", { params: { limit: 10 } });
      setLogs(r.data || []);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const count = logs?.length ?? 0;

  return (
    <div className="card p-3 mb-4" data-testid="impersonation-log-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 text-left"
        data-testid="impersonation-log-toggle"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-8 h-8 rounded-lg bg-violet-50 text-violet-700 flex items-center justify-center flex-shrink-0">
            <Eye size={14} />
          </span>
          <div className="min-w-0">
            <div className="text-xs text-[var(--ink-2)] uppercase tracking-wide">Recent impersonations</div>
            <div className="text-sm font-semibold text-[var(--ink)]">
              {loading ? "Loading…" : (count === 0 ? "No support sessions yet" : `${count} recent session${count === 1 ? "" : "s"}`)}
            </div>
          </div>
        </div>
        {open ? <ChevronUp size={16} className="text-[var(--ink-2)]" /> : <ChevronDown size={16} className="text-[var(--ink-2)]" />}
      </button>

      {open && (
        <div className="mt-3 pt-3 border-t" data-testid="impersonation-log-list">
          {loading ? (
            <div className="py-4 text-center text-[var(--ink-2)] text-sm">
              <Loader2 size={14} className="inline animate-spin" /> Loading…
            </div>
          ) : count === 0 ? (
            <div className="py-4 text-center text-[var(--ink-3)] text-sm">
              No-one has impersonated anyone yet. The audit trail will appear here.
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {logs.map((row) => (
                <div
                  key={row.id}
                  className="flex items-center justify-between gap-3 text-sm border-b pb-2 last:border-b-0"
                  data-testid={`impersonation-log-row-${row.id}`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 truncate">
                      <span className="font-semibold text-violet-700 truncate max-w-[180px]" title={row.admin_email}>
                        {row.admin_email}
                      </span>
                      <span className="text-[var(--ink-3)] text-xs">→</span>
                      <span className="font-mono text-xs truncate max-w-[200px]" title={row.target_email}>
                        {row.target_email}
                      </span>
                    </div>
                    <div className="text-xs text-[var(--ink-2)] mt-0.5">
                      {row.ttl_minutes}-min session
                    </div>
                  </div>
                  <div className="text-xs text-[var(--ink-3)] text-right whitespace-nowrap">
                    {row.created_at ? new Date(row.created_at).toLocaleString() : "—"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
